import { createBrowserRouter, redirect } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { PublicRoute } from "./PublicRoute";
import { AppShell } from "@/components/layout/AppShell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LoginPage } from "@/features/auth/LoginPage";
import { lazy, Suspense } from "react";
import { LoadingState } from "@/components/LoadingState";

const ObjectTypesAdminPage = lazy(() => import("@/features/schema-admin/ObjectTypesAdminPage").then(m => ({ default: m.ObjectTypesAdminPage })));
const ObjectTypeEditorPage = lazy(() => import("@/features/schema-admin/ObjectTypeEditorPage").then(m => ({ default: m.ObjectTypeEditorPage })));
const RelationshipTypesAdminPage = lazy(() => import("@/features/schema-admin/RelationshipTypesAdminPage").then(m => ({ default: m.RelationshipTypesAdminPage })));
const TagsAdminPage = lazy(() => import("@/features/schema-admin/TagsAdminPage").then(m => ({ default: m.TagsAdminPage })));
const EntityBrowserPage = lazy(() => import("@/features/browser/EntityBrowserPage").then(m => ({ default: m.EntityBrowserPage })));
const EntityDetailPage = lazy(() => import("@/features/detail/EntityDetailPage").then(m => ({ default: m.EntityDetailPage })));
const GraphPage = lazy(() => import("@/features/graph/GraphPage").then(m => ({ default: m.GraphPage })));
const MapPage = lazy(() => import("@/features/map/MapPage").then(m => ({ default: m.MapPage })));
const SearchResultsPage = lazy(() => import("@/features/search/SearchResultsPage").then(m => ({ default: m.SearchResultsPage })));
const CasesListPage = lazy(() => import("@/features/cases/CasesListPage").then(m => ({ default: m.CasesListPage })));
const CaseDetailPage = lazy(() => import("@/features/cases/CaseDetailPage").then(m => ({ default: m.CaseDetailPage })));
const TimelinePage = lazy(() => import("@/features/timeline/TimelinePage").then(m => ({ default: m.TimelinePage })));
const EnrichmentProvidersAdminPage = lazy(() => import("@/features/schema-admin/EnrichmentProvidersAdminPage").then(m => ({ default: m.EnrichmentProvidersAdminPage })));

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-24">
      <h1 className="text-4xl font-bold">404</h1>
      <p className="text-text-secondary">Page not found</p>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary><NotFoundPage /></ErrorBoundary>,
    children: [
      { index: true, loader: () => redirect("/cases") },
      { path: "cases", element: <SuspenseWrap><CasesListPage /></SuspenseWrap> },
      { path: "cases/:caseId", element: <SuspenseWrap><CaseDetailPage /></SuspenseWrap> },
      { path: "types", element: <SuspenseWrap><ObjectTypesAdminPage /></SuspenseWrap> },
      { path: "types/:typeId", element: <SuspenseWrap><EntityBrowserPage /></SuspenseWrap> },
      { path: "types/:typeId/edit", element: <SuspenseWrap><ObjectTypeEditorPage /></SuspenseWrap> },
      { path: "entities/:instanceId", element: <SuspenseWrap><EntityDetailPage /></SuspenseWrap> },
      { path: "entities/:instanceId/graph", element: <SuspenseWrap><GraphPage /></SuspenseWrap> },
      { path: "graph", element: <SuspenseWrap><GraphPage /></SuspenseWrap> },
      { path: "map", element: <SuspenseWrap><MapPage /></SuspenseWrap> },
      { path: "timeline", element: <SuspenseWrap><TimelinePage /></SuspenseWrap> },
      { path: "search", element: <SuspenseWrap><SearchResultsPage /></SuspenseWrap> },
      { path: "admin/relationship-types", element: <SuspenseWrap><RelationshipTypesAdminPage /></SuspenseWrap> },
      { path: "admin/tags", element: <SuspenseWrap><TagsAdminPage /></SuspenseWrap> },
      { path: "admin/enrichment", element: <SuspenseWrap><EnrichmentProvidersAdminPage /></SuspenseWrap> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
